import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QButton,
  QEmptyState,
  QEmptyStateButtonGroup,
  QEmptyStateSecondaryButtonGroup,
  QStack,
  useCurrentUser,
} from '@qualio/ui-components';
import { QEmptyStateProps } from '@qualio/ui-components/lib/QComponents/EmptyState/EmptyState';

import { EditorMode } from '../../../util/EditModeUtils';
import { DocumentOverviewContext } from '../DocumentOverview/Context';
import { canUserEditDocument } from '../DocumentOverview/RenderLogic';

type TrainingAssessmentEmptyStateProps = {
  onClick?: () => void;
};

export const TrainingAssessmentEmptyState: React.FC<
  TrainingAssessmentEmptyStateProps
> = ({ onClick }) => {
  const { t } = useTranslation();
  const { currentEditorMode, qualioDocument } = useContext(
    DocumentOverviewContext,
  );
  const currentUser = useCurrentUser();
  const canUserEdit = canUserEditDocument(qualioDocument, currentUser);
  const isMultiRootEditorEnabled =
    qualioDocument?.editor_version?.startsWith('multiroot');

  const isEditMode = isMultiRootEditorEnabled
    ? canUserEdit
    : currentEditorMode === EditorMode.EDIT;

  const emptyStateProps: QEmptyStateProps = {
    subtitle: t('emptyStates.trainingAssessmentsCanBeCreatedWhenEditing'),
    title: t('emptyStates.noTrainingAssessmentCreated'),
  };

  return (
    <QStack data-cy="training-assessment-empty-state" gap="4">
      {isEditMode ? (
        <div data-cy="create-training-assessment-button">
          <QEmptyState {...emptyStateProps}>
            <QEmptyStateButtonGroup>
              <QEmptyStateSecondaryButtonGroup>
                <QButton
                  data-cy="create-training-assessment-btn"
                  variant="outline"
                  leftIcon="Plus"
                  onClick={onClick}
                >
                  {t('buttons.createTrainingAssessment')}
                </QButton>
              </QEmptyStateSecondaryButtonGroup>
            </QEmptyStateButtonGroup>
          </QEmptyState>
        </div>
      ) : (
        <QEmptyState {...emptyStateProps} />
      )}
    </QStack>
  );
};
